import {
  Button,
  Grid,
  Loader,
  ScrollView,
  SearchField,
  Text,
} from "@aws-amplify/ui-react";
import { API, graphqlOperation } from "aws-amplify";

import { useContext, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import LocalizedStrings from "react-localization";
import moment from "moment";
import {
  CCard,
  CCardBody,
  CCollapse,
  CModal,
  CModalBody,
  CModalHeader,
  CModalTitle,
  CTable,
} from "@coreui/react";

import {
  equipmentParametersByParameterID,
  getEquipment,
  listCompanies,
  listParameters,
} from "graphql/queries";
import { updateEquipmentParameter, updateParameter } from "graphql/mutations";
import usePermissions from "hooks/usePermission";
import { ParameterCreateForm, ParameterUpdateForm } from ".";
import ConfirmModal from "components/utils/ConfirmModal";
import LoaderFullScreen from "components/utils/LoaderFullScreen";
import { LayoutContext } from "layout/context/LayoutContext";
import CompanyBranchFilter from "components/companies/CompanyBranchFilter";

const Parameters = () => {
  const { me } = useContext(LayoutContext);
  const [canCreate, canUpdate, canDelete] = usePermissions([
    "parameter:create",
    "parameter:update",
    "parameter:delete",
  ]);
  const [parameters, setParameters] = useState([]);
  const [parametersLoading, setParametersLoading] = useState(false);
  const [parameterDetails, setParameterDetails] = useState();
  const [modalDetails, setModalDetails] = useState({
    visible: false,
    parameterId: null,
    parameterName: null,
  });
  const [modalParameter, setModalParameter] = useState({
    visible: false,
    parameterId: null,
  });
  const [searchBy, setSearchBy] = useState({
    company: {
      id: "",
      display: "",
    },
  });
  const [loadingPage, setLoadingPage] = useState(false);
  const columnsMapped = useMemo(
    () =>
      parameters.map((item) => ({
        ...item,
        companiesNames: item.companiesNames || <Loader />,
        type: strings.details.type.options[item.type],
        actions: (
          <>
            <Button
              className="px-1"
              children={
                parameterDetails?.id === item.id
                  ? strings.parameters.hide
                  : strings.parameters.show
              }
              variation="link"
              size="small"
              onClick={() => {
                parameterDetails?.id === item.id
                  ? setParameterDetails(null)
                  : setParameterDetails(item);
              }}
            />
            {canUpdate && (
              <Button
                className="px-1"
                children={strings.parameters.edit}
                variation="link"
                size="small"
                onClick={() => openModal("update", item.id)}
              />
            )}
            {canDelete && (
              <Button
                className="px-1"
                children={strings.parameters.delete}
                variation="link"
                size="small"
                onClick={() => {
                  loadDelete(item.id, item.name);
                }}
              />
            )}
          </>
        ),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [parameters, parameterDetails?.id]
  );

  const onKeywordSubmit = (keyword) => {
    const keywordSplit = keyword && keyword.split(" ");
    fetchParameters(undefined, undefined, keywordSplit);
  };
  const openModal = (action, id) =>
    setModalParameter({ visible: true, action, parameterId: id });
  const onSuccessParameter = () => {
    setModalParameter({ visible: false });
    fetchParameters();
  };

  const loadDelete = async (id, name) => {
    try {
      setLoadingPage(true);
      const results = await API.graphql(
        graphqlOperation(equipmentParametersByParameterID, {
          parameterID: id,
          filter: { deletedAt: { attributeExists: false } },
        })
      );
      const resultsData = results.data.equipmentParametersByParameterID.items;

      let equipments = [];

      if (resultsData.length > 0) {
        await Promise.all(
          resultsData.map(async (resEq) => {
            const equipmentData = await API.graphql(
              graphqlOperation(getEquipment, { id: resEq.equipmentID })
            );
            if (equipmentData.data.getEquipment) {
              equipments.push(equipmentData.data.getEquipment);
            }
          })
        );

        equipments = equipments.map((eq) => eq.name);
      }
      setModalDetails({
        visible: true,
        parameterId: id,
        parameterName: name,
        equipments,
      });
    } catch (err) {
      console.log("Error al cargar equipos", err);
    } finally {
      setLoadingPage(false);
    }
  };

  async function fetchCompaniesNames(ids) {
    try {
      const companiesData = await API.graphql(
        graphqlOperation(listCompanies, {
          filter: {
            or: ids.map((id) => ({ id: { eq: id } })),
          },
        })
      );
      const companies = companiesData.data.listCompanies?.items || [];
      return companies?.map((c) => c.name)?.join(", ") || "";
    } catch (err) {
      console.log("Error fetchCompaniesNames", err);
      return "";
    }
  }

  const getNamesByParameters = (list) => {
    list?.forEach(async (item) => {
      const companiesNames = await fetchCompaniesNames(item.companies);

      setParameters((prevList) => {
        const updatedList = [...prevList];
        const index = updatedList.findIndex(
          (itemInd) => itemInd.id === item.id
        );

        updatedList[index] = {
          ...updatedList[index],
          companiesNames,
        };

        return updatedList;
      });
    });
  };

  async function fetchParameters(keywords) {
    let objFilter = {
      deletedAt: { attributeExists: false },
    };
    try {
      setParametersLoading(true);

      if (keywords) {
        objFilter = {
          ...objFilter,
          and: keywords.map((key) => ({
            or: [
              {
                name: { contains: key },
              },
              {
                description: { contains: key },
              },
            ],
          })),
        };
      }

      if (!!searchBy.company.id) {
        objFilter.and = [
          ...(objFilter.and || []),
          {
            companies: {
              contains: searchBy.company.id,
            },
          },
        ];
      } else if (!!me.companyID || me.managedCompanies.length > 0) {
        objFilter.and = [
          ...(objFilter.and || []),
          {
            or: [
              {
                companies: {
                  contains: me.companyID,
                },
              },
              ...me.managedCompanies.map((managed) => ({
                companies: {
                  contains: managed,
                },
              })),
            ],
          },
        ];
      }

      const parametersData = await API.graphql(
        graphqlOperation(listParameters, {
          filter: objFilter,
        })
      );
      const parametersItems = parametersData.data.listParameters.items;
      setParameters(parametersItems);
      getNamesByParameters(parametersItems);
      setParametersLoading(false);
    } catch (err) {
      toast.error(strings.responses.errorFetch);
      setParametersLoading(false);
    }
  }
  async function parameterDelete(id) {
    try {
      await equipmentParametersDelete(id);

      const result = await API.graphql(
        graphqlOperation(updateParameter, {
          input: { id, deletedAt: new Date().toISOString() },
        })
      );
      toast.success(strings.successDeleted);
      const deletedId = result.data.updateParameter.id;
      setParameters(parameters.filter((item) => item.id !== deletedId));
      setModalDetails({
        visible: false,
        parameterId: null,
      });
    } catch (err) {
      toast.error(strings.responses[err]);
    }
  }

  async function equipmentParametersDelete(parameterID) {
    try {
      const results = await API.graphql(
        graphqlOperation(equipmentParametersByParameterID, {
          parameterID,
          filter: { deletedAt: { attributeExists: false } },
        })
      );
      const resultsToDelete =
        results.data.equipmentParametersByParameterID.items;
      await Promise.all(
        resultsToDelete?.map((res) =>
          API.graphql(
            graphqlOperation(updateEquipmentParameter, {
              input: { id: res.id, deletedAt: new Date().toISOString() },
            })
          )
        )
      );
    } catch (err) {
      console.log("Error al eliminar relaciones", err);
    }
  }

  useEffect(() => {
    fetchParameters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchBy.company.id]);

  const tableColumns = [
    {
      key: "companiesNames",
      label: strings.table.headers.companiesNames,
      _style: { width: "20%" },
    },
    {
      key: "name",
      label: strings.table.headers.name,
      _style: { width: "20%" },
    },
    {
      key: "type",
      label: strings.table.headers.type,
      _style: { width: "10%" },
    },
    {
      key: "abbreviation",
      label: strings.table.headers.abbreviation,
      _style: { width: "10%" },
    },
    {
      key: "unit",
      label: strings.table.headers.unit,
      _style: { width: "10%" },
    },
    {
      key: "defaultValue",
      label: strings.table.headers.defaultValue,
      _style: { width: "10%" },
    },
    {
      key: "actions",
      label: "",
      _style: { width: "20%" },
    },
  ];

  return (
    <div>
      <span className="fs-3">{strings.title}</span>
      {canCreate && (
        <Button
          style={styles.buttonAdd}
          children={strings.parameters.addParameter}
          variation="primary"
          onClick={() => openModal("create")}
        />
      )}
      <div className="p-3">
        <Grid
          className="mb-3"
          templateColumns={{ medium: "1fr", xl: "1fr 1fr 1fr" }}
          rowGap="15px"
          columnGap="15px"
        >
          <CompanyBranchFilter
            searchBy={searchBy}
            setSearchBy={setSearchBy}
            onlyCompany
          />
          <SearchField
            label={strings.table.search.label}
            placeholder={strings.table.search.placeholder}
            onSubmit={onKeywordSubmit}
            onClear={onKeywordSubmit}
          />
        </Grid>
        {parametersLoading ? (
          <Loader />
        ) : (
          <>
            <CTable
              className="table-centered"
              items={columnsMapped}
              columns={tableColumns}
              tableHeadProps={{ color: "light" }}
              responsive
              hover
            />
          </>
        )}
        <CCollapse visible={!!parameterDetails?.id} style={styles.collapseCard}>
          <CCard key={parameterDetails?.id}>
            <CCardBody>
              <h4 className="text-muted">
                {strings.details.title} "{parameterDetails?.name}"
              </h4>
              <p className="text-muted">
                <b>{strings.details.description}:</b>{" "}
                {parameterDetails?.description}
              </p>
              <p className="text-muted">
                <b>{strings.details.type.label}:</b>{" "}
                {strings.details.type.options[parameterDetails?.type]}
              </p>
              <p className="text-muted">
                <b>{strings.details.abbreviation}:</b>{" "}
                {parameterDetails?.abbreviation}
              </p>
              <p className="text-muted">
                <b>{strings.details.unit}:</b> {parameterDetails?.unit}
              </p>
              <p className="text-muted">
                <b>{strings.details.defaultValue}:</b>{" "}
                {parameterDetails?.defaultValue}
              </p>
              <p className="text-muted">
                <b>{strings.details.createdAt}:</b>{" "}
                {moment(parameterDetails?.createdAt).format(
                  "YYYY-MM-DD HH:mm:ss"
                )}
              </p>
            </CCardBody>
          </CCard>
        </CCollapse>
      </div>
      <ConfirmModal
        visible={modalDetails.visible}
        body={
          <>
            <span>
              {strings.formatString(strings.modal.confirmDeleteQuestion, {
                name: modalDetails.parameterName,
              })}
            </span>
            {modalDetails.equipments?.length > 0 && (
              <p className="mt-3 mb-1">
                {strings.modal.parameterInUse}:
                <br />
                <Text className="text-muted">
                  {modalDetails.equipments.join(", ")}
                </Text>
              </p>
            )}
          </>
        }
        onCancel={() =>
          setModalDetails({
            visible: false,
          })
        }
        onConfirm={() => parameterDelete(modalDetails.parameterId)}
      />
      {modalParameter.action && (
        <CModal
          size="xl"
          alignment="center"
          visible={modalParameter.visible}
          onClose={() => setModalParameter({ visible: false })}
          backdrop="static"
        >
          <CModalHeader>
            <CModalTitle>
              {modalParameter.action === "create"
                ? strings.modalParameter.addTitle
                : strings.modalParameter.updateTitle}
            </CModalTitle>
          </CModalHeader>
          <CModalBody style={{ overflow: "hidden" }}>
            <ScrollView maxHeight="75vh">
              {modalParameter.action === "create" ? (
                <ParameterCreateForm onSuccess={onSuccessParameter} />
              ) : (
                <ParameterUpdateForm
                  parameterId={modalParameter.parameterId}
                  onSuccess={onSuccessParameter}
                />
              )}
            </ScrollView>
          </CModalBody>
        </CModal>
      )}
      {loadingPage && <LoaderFullScreen />}
    </div>
  );
};

const strings = new LocalizedStrings({
  es: {
    title: "Parámetros",
    successDeleted: "Parámetro eliminado con éxito",
    parameters: {
      addParameter: "Agregar Parámetro",
      show: "Ver",
      hide: "Ocultar",
      edit: "Editar",
      delete: "Eliminar",
    },
    responses: {
      errorFetch: "Error al consultar parámetro",
    },
    table: {
      headers: {
        companiesNames: "Empresas",
        name: "Nombre",
        type: "Tipo",
        abbreviation: "Abreviatura",
        unit: "Unidad",
        defaultValue: "Valor por defecto",
        actions: "Acciones",
      },
      pagination: {
        buttonPrevious: "Atrás",
        buttonNext: "Siguiente",
      },
      search: {
        label: "Buscar",
        placeholder: "Buscar por palabra clave...",
      },
    },
    details: {
      title: "Detalles del Parámetro",
      type: {
        label: "Tipo",
        options: {
          date: "Fecha",
          number: "Número",
          text: "Texto",
          list: "Lista",
        },
      },
      description: "Descripción",
      abbreviation: "Abreviatura",
      unit: "Unidad",
      defaultValue: "Valor por defecto",
      createdAt: "Fecha de creación",
    },
    modal: {
      confirmDeleteQuestion: "¿Estás seguro de eliminar el parámetro {name}?",
      cancelButton: "Cancelar",
      confirmButton: "Confirmar",
      parameterInUse:
        "El parametro está siendo utilizado por el/los siguiente(s) equipo(s)",
    },
    modalParameter: {
      addTitle: "Agregar Parámetro",
      updateTitle: "Actualizar Parámetro",
    },
  },
});

const styles = {
  buttonAdd: { float: "right" },
  modal: { width: "250px" },
  collapseCard: { marginTop: "15px" },
  buttonPrevious: { marginLeft: "15px" },
  buttonNext: { float: "right", marginRight: "15px" },
  searchByKeyword: { marginBottom: "15px", width: "25%", minWidth: "300px" },
};

export default Parameters;
